<!--
  V 0.2.0 - 2026-04-01
    - Added tooltip support and dynamic event marker y-values based on nearby speed points.
    - Added x axis time stamps and improved styling.
  V 0.1.0 - 2026-03-18
     Used SpeedSparkline to develop a sparkline that added events
-->
<template>
  <div class="sparkline-wrapper">
    <div class="sparkline-inner" >
      <canvas ref="chart"   style="height: 100%; width: 100%;" ></canvas>
        <div v-if="speeds.length" class="label">
          Max: {{ maxSpeed.toFixed(1) }} km/h
        </div>
    </div>
 </div>
</template>

<script>
/* eslint-disable no-unused-expressions */

import {
  Chart as ChartJS,
  LineElement,
  LinearScale,
  PointElement,
  TimeScale
} from 'chart.js'
import 'chartjs-adapter-date-fns' // or luxon adapter

// import { enUS } from 'date-fns/locale'

ChartJS.register(LineElement, LinearScale, PointElement, TimeScale)

export default {
  props: {
    tripData: Object,
    threshold: {
      type: Number,
      default: 80
    }

  },

  data: () => ({
    events: [], // array of
    chart: null,
    speeds: [], // Array of speed values numbers
    timestamps: [] // array of Date or ISO strings
  }),

  computed: {
    maxSpeed () {
      return this.speeds.length ? Math.max(...this.speeds) : 0
    },

    // Prepare Chart.js-friendly data points for speed
    speedData () {
      return this.speeds.map((y, i) => ({
        x: this.timestamps[i] || null,
        y
      })).filter(pt => pt.x !== null)
    },

    // Prepare event markers (placed at the speed y-value at that time, or fixed y)
    eventData () {
      if (!this.eventTimestamps.length) return []

      return this.eventTimestamps.map(ts => {
        // Optional: find closest speed point's y-value (simple nearest neighbor)
        let closestY = 0
        let minDiff = Infinity

        this.timestamps.forEach((t, i) => {
          const diff = Math.abs(new Date(t) - new Date(ts))
          if (diff < minDiff) {
            minDiff = diff
            closestY = this.speeds[i]
          }
        })

        return { x: ts, y: closestY } // or fixed: y: this.threshold * 0.8
      })
    }
  },

  watch: {
    tripData: {
      immediate: true,
      handler (trip) {
        if (!trip?.Points) {
          this.events = []
          this.eventTimestamps = []

          this.speeds = []
          this.timestamps = []

          this.renderChart()
          return
        }
        try {
          const points = JSON.parse(trip.Points)
          this.speeds = points.map(p => Number(p.spd) || 0)
          this.timestamps = points.map(p => p.tm || p.timestamp || null) // adjust field name if needed

          const events = JSON.parse(trip.Violations)
          this.events = events.map(e => Number(e.Id) || 0)
          this.eventTimestamps = events.map(p => p.Date || p.timestamp || null) //  .Events ? JSON.parse(trip.Events) : []  // ← parse event timestamps if provided

          this.$nextTick(this.renderChart)
        } catch {
          this.events = []
          this.eventTimestamps = []

          this.speeds = []
          this.timestamps = []
          this.renderChart()
        }
      }
    },
    threshold () { this.renderChart() }
    // You can also watch eventTimestamps if it changes separately
  },

  beforeDestroy () {
    this.chart?.destroy()
  },

  methods: {
    renderChart () {
      if (!this.$refs.chart) return
      this.chart?.destroy()

      const hasData = this.speedData.length > 0

      this.chart = new ChartJS(this.$refs.chart, {
        type: 'line',
        data: {
          datasets: [
            // Binary events as markers
            {
              label: 'Events',
              data: this.eventData,
              type: 'line',
              borderWidth: 0,
              pointRadius: ctx => ctx.raw ? 12 : 0,
              pointHoverRadius: 9,
              pointBackgroundColor: 'yellow', //'#475569' , //'#ef4444',
              pointBorderColor: 'black', //'#fff',
              pointBorderWidth: 3,
              pointStyle: 'triangle',
              showLine: false
            },
            // Main speed line
            {
              label: 'Speed',
              data: hasData ? this.speedData : [{ x: new Date(), y: 0 }],
              borderColor: '#3b82f6',
              backgroundColor: 'rgba(59,130,246,0.08)',
              borderWidth: 2,
              pointRadius: 0,
              tension: 0.18,
              fill: true,
              segment: {
                borderColor: ctx => {
                  const prev = ctx.p0.parsed.y
                  const curr = ctx.p1.parsed.y
                  return (prev >= this.threshold || curr >= this.threshold)
                    ? '#ef4444'
                    : '#3b82f6'
                }
              }
            }

          ]
        },
        options: {
          mode: 'nearest',
          intersect: false,
          //
          responsive: false,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { enabled: true } // enable if you want time/speed in tooltip
          },
          scales: {
            x: {
              type: 'time', // ← key change
              display: true, // true,
              time: {
                parser: 'yyyy-MM-dd\'T\'HH:mm:ss.SSS\'Z\'', // usually not needed with ISO
                tooltipFormat: 'HH:mm', // optional if you enable tooltips
                unit: 'minute', // or 'hour', etc. — Chart.js auto-detects best
                displayFormats: {
                  minute: 'HH:mm',
                  hour: 'HH:mm'
                }
              },
              // Optional padding
              ticks: { maxTicksLimit: 8 }
              // adapters: { date: { locale: 'en-ZA' } } // optional
            },
            y: {
              display: false,
              beginAtZero: true,
              suggestedMax: Math.max(10, this.threshold * 1.2) //160, //Math.max(10, this.threshold * 1.2)
            }
          },
          animation: { duration: 400 }
        }
      })
    }
  }
}
</script>

<style scoped>

.sparkline-wrapper-2 {
  position: relative;
  height: 120px;
  width: 100%;
   /* ← allows scrolling if chart is very wide */
/*    overflow-x: auto;
  -webkit-overflow-scrolling: touch;
 */}

.sparkline-wrapper {
  position: relative;
  height: 120px;
  width: 100%;
   /* ← allows scrolling if chart is very wide */
   overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.sparkline-inner {
  position: absolute;
  height: 120px;
  width: 100%;
   /* width is set inline via :style */
  /* width is set inline via :style */
}

.label {
  position: absolute;
  top: 6px;
  right: 10px;
  font-size: 12px;
  color: #475569;
  font-weight: 500;
  background: rgba(255,255,255,0.8);
  padding: 2px 6px;
  border-radius: 4px;
  pointer-events: none;
  z-index: 2;
}
</style>
